"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Result = void 0;
/**
 * Result Pattern Implementation (Extended)
 *
 * Represents the result of an operation that can either succeed or fail.
 * Supports typed error values for richer error handling.
 *
 * @template T - The type of the success value
 * @template E - The type of the error (default: string)
 */
class Result {
    isSuccess;
    error;
    _value;
    constructor(isSuccess, error, _value) {
        this.isSuccess = isSuccess;
        this.error = error;
        this._value = _value;
        if (isSuccess && error) {
            throw new Error('InvalidOperation: A result cannot be successful and contain an error');
        }
        if (!isSuccess && !error) {
            throw new Error('InvalidOperation: A failing result needs to contain an error message');
        }
        Object.freeze(this);
    }
    get isFailure() {
        return !this.isSuccess;
    }
    /**
   * Combine multiple results into one
   * Returns fail if any result is a failure
   */
    static combine(results) {
        for (const r of results) {
            if (r.isFailure)
                return Result.fail(r.error);
        }
        return Result.ok();
    }
    /**
   * Create a failed result
   */
    static fail(error) {
        return new Result(false, error);
    }
    /**
   * Create a successful result
   */
    static ok(value) {
        return new Result(true, undefined, value);
    }
    /**
   * Chain operations that return Results
   */
    flatMap(fn) {
        if (this.isFailure) {
            return Result.fail(this.error);
        }
        return fn(this.getValue());
    }
    /**
   * Get the value from a successful result
   * @throws Error if the result is a failure
   */
    getValue() {
        if (!this.isSuccess) {
            throw new Error('Cannot get the value of an error result. Use isFailure first.');
        }
        return this._value;
    }
    /**
   * Transform the value of a successful result
   */
    map(fn) {
        if (this.isFailure) {
            return Result.fail(this.error);
        }
        try {
            return Result.ok(fn(this.getValue()));
        }
        catch (error) {
            const err = (error instanceof Error ? error.message : 'Unknown error');
            return Result.fail(err);
        }
    }
}
exports.Result = Result;
